import { useEffect, useRef } from 'react';

import { useFieldA11y } from './form';

export interface PickerA11yProps {
  /** Accessible name when the picker is not inside a FormField. */
  'aria-label'?: string;
}

/**
 * The kit SearchSelect renders its own `<input>` and takes no id / aria
 * props, so a `<label htmlFor>` can't reach it. Name it from the enclosing
 * FormField (aria-labelledby → the field label) or an explicit `aria-label`,
 * and point it at the field's error / hint line. Attributes React doesn't
 * manage, set on the stable input element — no kit change needed.
 * TODO(kit): replace with `id` / `aria-*` props on SearchSelect.
 */
export const usePickerA11y = (
  ariaLabel: string | undefined,
  invalid: boolean | undefined
) => {
  const ref = useRef<HTMLDivElement>(null);
  const field = useFieldA11y();
  const labelledBy = field?.labelId;
  const describedBy = field?.describedBy;
  useEffect(() => {
    const input = ref.current?.querySelector('input');
    if (!input) return;
    const set = (name: string, value: string | undefined) => {
      if (value) input.setAttribute(name, value);
      else input.removeAttribute(name);
    };
    set('aria-labelledby', ariaLabel ? undefined : labelledBy);
    set('aria-label', ariaLabel);
    set('aria-describedby', describedBy);
    set('aria-invalid', invalid ? 'true' : undefined);
  }, [ariaLabel, labelledBy, describedBy, invalid]);
  return ref;
};
